import { TypeId } from '@unipile/db';
import { type PublicAccount } from '@unipile/types';
import { CardContent, CardDescription, CardHeader, CardTitle, Spinner, useAccount } from '@unipile/ui';
import { ArrowLeftRightIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ProviderIcons, ProviderNames } from '../../config/providers';

interface InitialSyncWaitingProps {
  account_id: TypeId<'account'>;
  app_id: TypeId<'app'>;
  onComplete: (account: PublicAccount) => void;
  brand_icon_url: string | undefined;
}

export function InitialSyncWaiting({ account_id, app_id, onComplete, brand_icon_url }: InitialSyncWaitingProps) {
  const { t } = useTranslation();

  const { data: accountData } = useAccount({
    queryConfig: { refetchInterval: 3000, retry: true },
    appId: app_id,
    accountId: account_id,
  });

  useEffect(() => {
    // Check if initial sync is completed or failed
    if (accountData?.initial_sync?.status === 'completed' || accountData?.initial_sync?.status === 'failed') {
      onComplete(accountData);
    }
  }, [accountData, onComplete]);

  if (!accountData) return null;

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-center gap-4">
        <img
          src={ProviderIcons[accountData.provider]}
          alt={ProviderNames[accountData.provider]}
          className="h-10 w-10 rounded-md"
        />
        {brand_icon_url && (
          <>
            <ArrowLeftRightIcon className="text-muted-foreground" />
            <img alt="logo" className="h-10 w-10 object-cover" src={brand_icon_url} />
          </>
        )}
      </div>
      <CardHeader className="text-center">
        <CardTitle>{t('InitialSync.title')}</CardTitle>
        <CardDescription>{t('InitialSync.description')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center">
          <Spinner className="h-12 w-12" />

          <p className="text-muted-foreground text-xs">{t('InitialSync.pleaseWait')}</p>
        </div>
      </CardContent>
    </div>
  );
}
